import { ShaderTheme } from '../../../types/shader';
import { PlanckShearShader } from './planckShear';
import { ParticulateVoidShader } from './particulateVoid';
import { QuantumGridShader } from './quantumGrid';
import { TemporalFreezeShader } from './temporalFreeze';
import { SynapticDecayShader } from './synapticDecay';

export interface ShaderUniforms {
  speed: number;
  intensity: number;
  distortion: number;
  gridDensity: number;
}

export interface ShaderRenderContext {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  dpr: number;
  time: number;
  deltaTime: number;
  uniforms: ShaderUniforms;
  reducedMotion?: boolean;
}

export interface ShaderModule {
  readonly name: ShaderTheme;
  render(context: ShaderRenderContext): void;
  reset?(): void;
  destroy?(): void;
}

export {
  PlanckShearShader,
  ParticulateVoidShader,
  QuantumGridShader,
  TemporalFreezeShader,
  SynapticDecayShader,
};

export const SHADER_MODES: readonly ShaderTheme[] = [
  'planck_shear',
  'particulate_void',
  'quantum_grid',
  'temporal_freeze',
  'synaptic_decay',
] as const;

export class ShaderRegistry {
  private readonly modules: Map<ShaderTheme, ShaderModule> = new Map();

  constructor() {
    this.modules.set('planck_shear', new PlanckShearShader());
    this.modules.set('particulate_void', new ParticulateVoidShader());
    this.modules.set('quantum_grid', new QuantumGridShader());
    this.modules.set('temporal_freeze', new TemporalFreezeShader());
    this.modules.set('synaptic_decay', new SynapticDecayShader());
  }

  get(theme: ShaderTheme): ShaderModule {
    const module = this.modules.get(theme);
    if (!module) {
      // Fallback to planck_shear if unknown theme passed
      return this.modules.get('planck_shear')!;
    }
    return module;
  }

  getAll(): ShaderModule[] {
    return Array.from(this.modules.values());
  }

  resetAll(): void {
    for (const mod of this.modules.values()) {
      if (mod.reset) mod.reset();
    }
  }
}
